export const JOURNEY_LIFECYCLE = {
  running: 'running',
  paused: 'paused',
  completed: 'completed',
  failed: 'failed',
};

export const STEP_STATUS = {
  pending: 'pending',
  waiting: 'waiting',
  active: 'active',
  completed: 'completed',
  skipped: 'skipped',
  failed: 'failed',
  timedOut: 'timedOut',
};

// Engine step states that say something the asset's own status does
// not (brief 2026-09-18): shown next to a survey step's assessment
// status. `pending` / `active` / `completed` add nothing there.
export const STEP_STATUSES_WORTH_SHOWING = [
  STEP_STATUS.waiting,
  STEP_STATUS.skipped,
  STEP_STATUS.failed,
  STEP_STATUS.timedOut,
];

export const DEFINITION_STATUS = {
  draft: 'draft',
  published: 'published',
  archived: 'archived',
};

// One action per thing a step sends (API 2026-10-12). The legacy
// `sendResource` is still accepted by the API as input but never
// returned, so it is not a value the dashboard ever sees or sends.
export const STEP_ACTIONS = {
  sendSurvey: 'sendSurvey',
  sendArticle: 'sendArticle',
  sendAudio: 'sendAudio',
  sendVideo: 'sendVideo',
};

export const ON_TIMEOUT_VALUES = { skip: 'skip', fail: 'fail', escalate: 'escalate' };

export const JOURNEY_POLL_INTERVAL_MS = 15000;

// Template description (API 2026-09-18): optional plain text, 422 above this.
export const PROGRAM_DESCRIPTION_MAX_LENGTH = 2000;

// FSD `<Badge tone>` mappings. Colour intent mirrors the dev lineage
// (running=lightBlue, paused=grey, completed=green, failed=red;
// draft=orange, published=green, archived=grey) re-expressed in the
// Clinical Status palette. Kept entity-local instead of extending
// @shared/lib/getStatusBadge: the lifecycle/status literals ('draft',
// 'completed', ...) collide with claim/assessment keys already in the
// central map.
const LIFECYCLE_TONE = {
  [JOURNEY_LIFECYCLE.running]: 'pending', // in flight (blue)
  [JOURNEY_LIFECYCLE.paused]: 'inactive', // neutral hold (grey)
  [JOURNEY_LIFECYCLE.completed]: 'normal', // success (green)
  [JOURNEY_LIFECYCLE.failed]: 'critical', // terminal failure (red)
};

const DEFINITION_TONE = {
  [DEFINITION_STATUS.draft]: 'moderate', // needs publishing (amber)
  [DEFINITION_STATUS.published]: 'normal', // live (green)
  [DEFINITION_STATUS.archived]: 'inactive', // retired (grey)
};

// Assessment statuses (entities/assessment ASSESSMENT_STATUSES) as they
// appear on a program step: same reading as the Assessments page.
//
// A resource step reports its own statuses too (ResourceStatus:
// scheduled / invited / engaged / canceled). Three of them are spelled
// the same as an assessment's and read the same way; `engaged` is the
// resource's end state, so it takes the tone `completed` has.
const ASSET_STATUS_TONE = {
  invited: 'inactive',
  created: 'inactive',
  scheduled: 'inactive',
  engaged: 'normal',
  staled: 'pending', // "Opened"
  inProgress: 'pending',
  pendingReview: 'moderate',
  partialProcessed: 'moderate',
  processing: 'moderate',
  completed: 'normal',
  eTransfer: 'normal',
  canceled: 'critical',
  noShow: 'critical',
};

const STEP_STATUS_TONE = {
  [STEP_STATUS.pending]: 'inactive',
  [STEP_STATUS.waiting]: 'pending',
  [STEP_STATUS.active]: 'pending',
  [STEP_STATUS.completed]: 'normal',
  [STEP_STATUS.skipped]: 'inactive',
  [STEP_STATUS.failed]: 'critical',
  [STEP_STATUS.timedOut]: 'critical',
};

export const getJourneyLifecycleTone = state => LIFECYCLE_TONE[state] || 'inactive';
export const getAssetStatusTone = status => ASSET_STATUS_TONE[status] || 'inactive';
export const getStepStatusTone = status => STEP_STATUS_TONE[status] || 'inactive';
export const getDefinitionStatusTone = status => DEFINITION_TONE[status] || 'inactive';
